import React, { useEffect, useState } from 'react';
import { Check, ChevronLeft, ChevronRight, Download, LogOut, Moon, PlusSquare, Share, Smartphone } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { Switch } from '@/components/ui/switch';

export interface LibraryAccountSheetProps {
  name: string | null;
  email: string | null;
  avatarUrl?: string | null;
  isDarkMode: boolean;
  onToggleDarkMode: () => void;
  onSignOut: () => void | Promise<void>;
  /** "Use as an app": install it (where the browser can) or learn how; hidden when unknown. */
  app?: AppModeProps;
  /** "Save from any app": the Shortcut setup (QuickSaveSetup), shown as the sheet's own page; hidden when absent. */
  quickSave?: React.ReactNode;
}

export interface AppModeProps {
  /** Already running from the home screen. */
  installed: boolean;
  /** The browser offered an install prompt (Android, desktop Chrome). */
  canPrompt: boolean;
  platform: 'ios' | 'android' | 'other';
  onInstall: () => Promise<boolean>;
}

function initialsOf(name: string | null, email: string | null): string {
  const fromName = (name ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('');
  return (fromName || email?.charAt(0) || 'U').toUpperCase();
}

/**
 * The header's avatar and the bottom sheet it opens on a phone: who is
 * signed in, dark mode, sign out. Pure props; MobileLibraryContainer wires
 * it to useAuth and the store. Replaces the desktop UserMenu, whose
 * dropdown opens sideways off a phone screen.
 */
export function LibraryAccountSheet({ name, email, avatarUrl, isDarkMode, onToggleDarkMode, onSignOut, app, quickSave }: LibraryAccountSheetProps) {
  const [open, setOpen] = useState(false);
  // The sheet's other pages: how to add Mindfull to the home screen, and the Shortcut setup
  const [view, setView] = useState<'main' | 'install' | 'quickSave'>('main');
  useEffect(() => {
    if (!open) setView('main');
  }, [open]);
  const [signingOut, setSigningOut] = useState(false);
  const initials = initialsOf(name, email);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Your account"
        aria-haspopup="dialog"
        className="flex h-9 w-9 items-center justify-center rounded-full outline-none transition-transform duration-150 ease-out active:scale-[0.97] focus-visible:ring-2 focus-visible:ring-signal motion-reduce:transition-none"
      >
        <Avatar className="h-8 w-8 border border-line">
          {avatarUrl && <AvatarImage src={avatarUrl} alt="" />}
          <AvatarFallback className="bg-paper-sunken text-[13px] font-medium text-ink-2">{initials}</AvatarFallback>
        </Avatar>
      </button>

      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent className="border-line bg-paper-raised">
          <DrawerHeader className="flex items-center gap-3 text-left">
            <Avatar className="h-11 w-11 border border-line">
              {avatarUrl && <AvatarImage src={avatarUrl} alt="" />}
              <AvatarFallback className="bg-paper-sunken text-[15px] font-medium text-ink-2">{initials}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <DrawerTitle className="truncate font-display text-[17px] text-ink">{name || 'Your account'}</DrawerTitle>
              <DrawerDescription className="truncate text-[13px] text-ink-3">{email || 'Signed in'}</DrawerDescription>
            </div>
          </DrawerHeader>

          {view === 'install' && app ? (
            <InstallSteps platform={app.platform} onBack={() => setView('main')} />
          ) : view === 'quickSave' && quickSave ? (
            <div className="flex max-h-[70vh] flex-col overflow-y-auto px-4 pb-[calc(16px+env(safe-area-inset-bottom))]">
              <BackButton onBack={() => setView('main')} />
              {quickSave}
            </div>
          ) : (
          <div className="flex flex-col px-4 pb-[calc(16px+env(safe-area-inset-bottom))]">
            {app && (
              <div className="mb-3 overflow-hidden rounded-[12px] border border-line bg-paper">
                {app.installed ? (
                  <div className="flex h-14 items-center gap-3 px-4">
                    <Smartphone className="h-[18px] w-[18px] text-ink-2" strokeWidth={1.75} aria-hidden />
                    <span className="flex-1 text-[15px] text-ink">Using the app</span>
                    <Check className="h-4 w-4 text-ink-2" strokeWidth={2} aria-hidden />
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={async () => {
                      if (app.canPrompt && (await app.onInstall())) setOpen(false);
                      else if (!app.canPrompt) setView('install');
                    }}
                    className="flex min-h-14 w-full items-center gap-3 px-4 py-2.5 text-left outline-none transition-colors duration-150 ease-out active:bg-wash focus-visible:bg-wash"
                  >
                    <Smartphone className="h-[18px] w-[18px] shrink-0 text-ink-2" strokeWidth={1.75} aria-hidden />
                    <span className="min-w-0 flex-1">
                      <span className="block text-[15px] text-ink">Use as an app</span>
                      <span className="block text-[12px] leading-[16px] text-ink-3">Full screen from your home screen, no address bar</span>
                    </span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-ink-3" strokeWidth={2} aria-hidden />
                  </button>
                )}
              </div>
            )}
            {quickSave && (
              <div className="mb-3 overflow-hidden rounded-[12px] border border-line bg-paper">
                <button
                  type="button"
                  onClick={() => setView('quickSave')}
                  className="flex min-h-14 w-full items-center gap-3 px-4 py-2.5 text-left outline-none transition-colors duration-150 ease-out active:bg-wash focus-visible:bg-wash"
                >
                  <Download className="h-[18px] w-[18px] shrink-0 text-ink-2" strokeWidth={1.75} aria-hidden />
                  <span className="min-w-0 flex-1">
                    <span className="block text-[15px] text-ink">Save from any app</span>
                    <span className="block text-[12px] leading-[16px] text-ink-3">Add Save to Mindfull to your share sheet</span>
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-ink-3" strokeWidth={2} aria-hidden />
                </button>
              </div>
            )}
            <div className="overflow-hidden rounded-[12px] border border-line bg-paper">
              <label className="flex h-14 cursor-pointer items-center gap-3 px-4">
                <Moon className="h-[18px] w-[18px] text-ink-2" strokeWidth={1.75} aria-hidden />
                <span className="flex-1 text-[15px] text-ink">Dark mode</span>
                <Switch checked={isDarkMode} onCheckedChange={onToggleDarkMode} aria-label="Dark mode" />
              </label>
              <div className="h-px bg-line" />
              <button
                type="button"
                disabled={signingOut}
                onClick={async () => {
                  setSigningOut(true);
                  try {
                    await onSignOut();
                    setOpen(false);
                  } finally {
                    setSigningOut(false);
                  }
                }}
                className="flex h-14 w-full items-center gap-3 px-4 text-left text-[15px] text-ink outline-none transition-colors duration-150 ease-out active:bg-wash focus-visible:bg-wash disabled:opacity-60"
              >
                <LogOut className="h-[18px] w-[18px] text-ink-2" strokeWidth={1.75} aria-hidden />
                {signingOut ? 'Signing out' : 'Sign out'}
              </button>
            </div>
          </div>
          )}
        </DrawerContent>
      </Drawer>
    </>
  );
}

/** How to add Mindfull to the home screen, for browsers that cannot install it themselves (iPhone Safari above all). */
function InstallSteps({ platform, onBack }: { platform: AppModeProps['platform']; onBack: () => void }) {
  const steps =
    platform === 'ios'
      ? [
          { Icon: Share, text: 'Tap Share in Safari\'s toolbar' },
          { Icon: PlusSquare, text: 'Choose Add to Home Screen, then Add' },
          { Icon: Smartphone, text: 'Open Mindfull from your home screen' },
        ]
      : [
          { Icon: ChevronRight, text: "Open your browser's menu" },
          { Icon: PlusSquare, text: 'Choose Install app or Add to Home screen' },
          { Icon: Smartphone, text: 'Open Mindfull from your home screen' },
        ];
  return (
    <div className="flex flex-col px-4 pb-[calc(16px+env(safe-area-inset-bottom))]">
      <BackButton onBack={onBack} />
      <p className="mb-3 text-[15px] leading-[22px] text-ink">Mindfull opens full screen, like an app: no address bar, more room for your things.</p>
      <ol className="overflow-hidden rounded-[12px] border border-line bg-paper">
        {steps.map(({ Icon, text }, i) => (
          <li key={text} className={i > 0 ? 'flex h-14 items-center gap-3 border-t border-line px-4' : 'flex h-14 items-center gap-3 px-4'}>
            <span className="w-4 font-mono text-[12px] text-ink-3">{i + 1}</span>
            <Icon className="h-[18px] w-[18px] text-ink-2" strokeWidth={1.75} aria-hidden />
            <span className="text-[15px] text-ink">{text}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function BackButton({ onBack }: { onBack: () => void }) {
  return (
    <button
      type="button"
      onClick={onBack}
      className="-ml-1 mb-2 flex h-9 w-fit shrink-0 items-center gap-1 rounded-full pl-1 pr-3 text-[14px] text-ink-2 outline-none active:bg-wash focus-visible:ring-2 focus-visible:ring-signal"
    >
      <ChevronLeft className="h-4 w-4" strokeWidth={2} aria-hidden />
      Back
    </button>
  );
}
